import React, { useEffect, useState } from "react";
import Sidebar from "components/Sidebar/Sidebar.js";
import CardTable from "components/Cards/CardTable";
import axios from "axios";
import { TableDataItem } from "components/Cards/CardTable";
import { Spinner } from "components/Spinner";
import config from "services/config";
import {
  Button,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  TextField,
  InputAdornment,
  Paper,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import { useAuth } from "context/auth/auth";
import VerifiedIcon from "@mui/icons-material/Verified";
import { formatCLP } from "utils";
import {
  Download,
  Delete,
  Search,
  AddCircle,
  FitnessCenter,
  AttachMoney,
  ToggleOn,
  ToggleOff,
  CreditCard,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { useLayout } from "context/layout/layout";
import { colors } from "utils/theme";

const productsHeaders = ["Nombre", "ID", "Precio", "Status"];
const ACTIONS = ["Acciones"];

export function MembershipList({ color = null }) {
  const auth = useAuth();
  const layout = useLayout();
  const [memberships, setMemberships] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filteredMemberships, setFilteredMemberships] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMemberships();
  }, []);

  useEffect(() => {
    if (memberships && query.length > 0) {
      const filtered = memberships.filter(
        (m) =>
          m.title.toLowerCase().includes(query.toLowerCase()) ||
          m._id.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredMemberships(filtered);
    } else {
      setFilteredMemberships(memberships || []);
    }
  }, [query, memberships]);

  const fetchMemberships = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${config.url}/membership/list`);
      setMemberships(res.data);
      setFilteredMemberships(res.data);
    } catch (err) {
      console.log(err);
      layout.errorAlert({ message: "Error al cargar las membresías" });
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (M_ID) => {
    try {
      setLoading(true);
      await axios.get(`${config.url}/membership/change_status/${M_ID}`);
      await fetchMemberships();
      layout.successAlert({ message: "Estado actualizado correctamente" });
    } catch (err) {
      console.log(err);
      layout.errorAlert({ message: "Error al cambiar el estado" });
    } finally {
      setLoading(false);
    }
  };

  async function handleDeleteMembership(membershipId) {
    try {
      setLoading(true);
      await axios.post(`${config.url}/membership/remove/${membershipId}`);
      const filteredMemberships = memberships.filter(
        (m) => m._id !== membershipId
      );
      setMemberships(filteredMemberships);
      layout.successAlert({ message: "Membresía eliminada exitosamente" });
    } catch (err) {
      console.log(err);
      layout.errorAlert({ message: "Error al eliminar la membresía" });
    } finally {
      setLoading(false);
    }
  }

  const exportToXLSX = () => {
    if (!memberships || memberships.length === 0) return;

    const data = memberships.map((m) => ({
      Nombre: m.title,
      ID: m._id,
      Precio: formatCLP(m.price),
      Status: m.enabled === "true" ? "Habilitado" : "Deshabilitado",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Memberships");
    XLSX.writeFile(workbook, "memberships.xlsx");
  };

  const totalMemberships = memberships?.length || 0;
  const activeMemberships =
    memberships?.filter((m) => m.enabled === "true").length || 0;
  const inactiveMemberships = totalMemberships - activeMemberships;
  const averagePrice =
    memberships?.length > 0
      ? memberships.reduce((sum, m) => sum + m.price, 0) / memberships.length
      : 0;

  return (
    <>
      {loading && <Spinner />}
      <Sidebar />

      <Box
        sx={{
          marginLeft: "210px",
          minHeight: "100vh",
          backgroundColor: "#f8fafc",
        }}
      >
        {/* Header moderno */}
        <Box
          sx={{
            background: colors.sapphire,
            color: "white",
            padding: "2rem 0",
            marginBottom: "2rem",
          }}
        >
          <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: "0 2rem" }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", marginBottom: "0.5rem" }}
            >
              Gestión de Membresías
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Administra los planes y precios de tu negocio
            </Typography>
          </Box>
        </Box>

        <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: "0 2rem" }}>
          {/* Cards de estadísticas */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: 3,
              marginBottom: "2rem",
            }}
          >
            <Card
              sx={{
                background: colors.sapphire + " !important",
                color: "white",
                boxShadow: "0 10px 25px rgba(102, 126, 234, 0.3)",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: "bold", color: "white" }}>
                      {totalMemberships}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, color: "white" }}>
                      Total Planes
                    </Typography>
                  </Box>
                  <CreditCard sx={{ fontSize: 40, opacity: 0.8, color: "white" }} />
                </Box>
              </CardContent>
            </Card>

            <Card
              sx={{
                background: colors.sapphire + " !important",
                color: "white",
                boxShadow: "0 10px 25px rgba(17, 153, 142, 0.3)",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: "bold", color: "white" }}>
                      {activeMemberships}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, color: "white" }}>
                      Planes Activos
                    </Typography>
                  </Box>
                  <ToggleOn sx={{ fontSize: 40, opacity: 0.8, color: "white" }} />
                </Box>
              </CardContent>
            </Card>

            <Card
              sx={{
                background: colors.sapphire + "!important",
                color: "white",
                boxShadow: "0 10px 25px rgba(252, 70, 107, 0.3)",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: "bold", color: "white" }}>
                      {inactiveMemberships}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, color: "white" }}>
                      Planes Inactivos
                    </Typography>
                  </Box>
                  <ToggleOff sx={{ fontSize: 40, opacity: 0.8, color: "white" }} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Controles mejorados */}
          <Paper
            sx={{
              padding: "1.5rem",
              marginBottom: "2rem",
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <TextField
                placeholder="Buscar por nombre o ID..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{
                  minWidth: "300px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "25px",
                    backgroundColor: "#f8fafc",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#64748b" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  onClick={exportToXLSX}
                  variant="outlined"
                  startIcon={<Download />}
                  sx={{
                    borderRadius: "25px",
                    textTransform: "none",
                    fontWeight: 600,
                    borderColor: "#667eea",
                    color: "#667eea",
                    "&:hover": {
                      borderColor: "#667eea",
                      backgroundColor: "rgba(102, 126, 234, 0.1)",
                    },
                  }}
                >
                  Exportar
                </Button>

                {auth.isAdmin && (
                  <Button
                    variant="contained"
                    startIcon={<AddCircle />}
                    onClick={() => navigate("/membership/add")}
                    sx={{
                      borderRadius: "25px",
                      textTransform: "none",
                      fontWeight: 600,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                      "&:hover": {
                        boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                      },
                    }}
                  >
                    Nuevo Plan
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Tabla mejorada */}
          <Paper
            sx={{
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <CardTable
              title={"Planes de Membresía"}
              headers={
                auth.isAdmin
                  ? [...productsHeaders, ...ACTIONS]
                  : productsHeaders
              }
              add={null}
              routeadd={null}
              query={query}
              setQuery={setQuery}
              color={"light"}
            >
              {filteredMemberships.length > 0 &&
                filteredMemberships.map((m, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: "1px solid #e2e8f0",
                      "&:hover": { backgroundColor: "#f8fafc" },
                    }}
                  >
                    <TableDataItem
                      sx={{
                        flex: 1,
                        minWidth: 300,
                        width: 300,
                        maxWidth: 300,
                        fontWeight: 600,
                        fontSize: 14,
                        textAlign: "left",
                        paddingLeft: "16px",
                        color: "#1e293b",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          maxWidth: 300,
                        }}
                      >
                        <CreditCard sx={{ fontSize: 18, color: "#667eea" }} />
                        <Box
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}
                        >
                          {m.title}
                        </Box>
                      </Box>
                    </TableDataItem>

                    <TableDataItem
                      sx={{
                        fontSize: 12,
                        width: 140,
                        maxWidth: 140,
                        flexShrink: 0,
                        fontFamily: "monospace",
                        color: "#64748b",
                      }}
                    >
                      {m._id}
                    </TableDataItem>

                    <TableDataItem
                      sx={{
                        fontSize: 14,
                        width: 120,
                        maxWidth: 120,
                        flexShrink: 0,
                        fontWeight: 600,
                        color: "#059669",
                      }}
                    >
                      {formatCLP(m.price)}
                    </TableDataItem>

                    <TableDataItem
                      sx={{
                        fontSize: 12,
                        width: 120,
                        maxWidth: 120,
                        flexShrink: 0,
                      }}
                    >
                      <Chip
                        label={
                          m.enabled === "true" ? "Habilitado" : "Deshabilitado"
                        }
                        color={m.enabled === "true" ? "success" : "error"}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          minWidth: "90px",
                        }}
                      />
                    </TableDataItem>

                    {auth.isAdmin && (
  <TableDataItem
    sx={{ width: 140, maxWidth: 140, flexShrink: 0 }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "center", // centra horizontalmente
        alignItems: "center",     // centra verticalmente
        gap: 0.5,
        height: "100%",           // necesario si el padre tiene altura definida
      }}
    >
      <Tooltip
        title={
          m.enabled === "true"
            ? "Deshabilitar"
            : "Habilitar"
        }
      >
        <IconButton
          size="small"
          onClick={() => changeStatus(m._id)}
          sx={{
            color: "#64748b",
            "&:hover": {
              backgroundColor:
                m.enabled === "true"
                  ? "#fef2f2"
                  : "#f0fdf4",
              color:
                m.enabled === "true"
                  ? "#dc2626"
                  : "#16a34a",
            },
          }}
        >
          {m.enabled === "true" ? (
            <VerifiedIcon sx={{ fontSize: 18 }} />
          ) : (
            <BlockIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Tooltip>

      <Tooltip title="Eliminar Plan">
        <IconButton
          size="small"
          onClick={() => handleDeleteMembership(m._id)}
          sx={{
            color: "#64748b",
            "&:hover": {
              backgroundColor: "#fef2f2",
              color: "#dc2626",
            },
          }}
        >
          <Delete sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  </TableDataItem>
)}

                  </tr>
                ))}
            </CardTable>
          </Paper>

          {/* Footer con información adicional */}
          {filteredMemberships.length === 0 && !loading && (
            <Paper
              sx={{
                padding: "3rem",
                textAlign: "center",
                borderRadius: "12px",
                marginTop: "2rem",
              }}
            >
              <FitnessCenter
                sx={{ fontSize: 64, color: "#cbd5e1", marginBottom: 2 }}
              />
              <Typography
                variant="h6"
                sx={{ color: "#64748b", marginBottom: 1 }}
              >
                {query
                  ? "No se encontraron resultados"
                  : "No hay planes registrados"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#94a3b8", marginBottom: 3 }}
              >
                {query
                  ? "Intenta con otros términos de búsqueda"
                  : "Comienza creando tu primer plan de membresía"}
              </Typography>
              {!query && auth.isAdmin && (
                <Button
                  variant="contained"
                  startIcon={<AddCircle />}
                  onClick={() => navigate("/membership/add")}
                  sx={{
                    borderRadius: "25px",
                    textTransform: "none",
                    fontWeight: 600,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  }}
                >
                  Crear Primer Plan
                </Button>
              )}
            </Paper>
          )}
        </Box>
      </Box>
    </>
  );
}
